/**
 * Campaign Template Gallery
 * A modal gallery to select pre-built cold outreach email campaign templates
 * Based on design patterns from SequenceTemplateGallery
 *
 * Features:
 * - 42 pre-built campaign templates across 8 categories
 * - Filters by category, target audience, difficulty, and personalization level
 * - Search by name, description, and tags
 * - Two-panel layout with details preview
 */

import React, { useState, useMemo } from "react";
import {
  Modal as AntModal,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Input,
  Button,
  Select,
  Empty,
  Tooltip,
  Tabs,
} from "antd";
import {
  SearchOutlined,
  AimOutlined,
  BulbOutlined,
  SendOutlined,
  CalendarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  ReloadOutlined,
  BankOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  CodeOutlined,
  CrownOutlined,
  SolutionOutlined,
  TrophyOutlined,
  DollarOutlined,
  UserSwitchOutlined,
  GiftOutlined,
  ShopOutlined,
  ApiOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  AppstoreOutlined,
  FilterOutlined,
  UserOutlined,
  SafetyOutlined,
  ExperimentOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  NotificationOutlined,
  FundOutlined,
  GlobalOutlined,
  CommentOutlined,
  HeartOutlined,
  LikeOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  CloudOutlined,
  ToolOutlined,
  DisconnectOutlined,
  BellOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  ShareAltOutlined,
  FireOutlined,
  UndoOutlined,
  EyeInvisibleOutlined,
  SwapOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import {
  ALL_CAMPAIGN_TEMPLATES,
  CAMPAIGN_TEMPLATE_CATEGORIES,
} from "../campaignTemplates";
import type {
  CampaignTemplate,
  CampaignCategory,
  TargetAudience,
  PersonalizationLevel,
} from "../campaignTemplates/types";

// Cast Modal to any to avoid TypeScript issues with Emotion CSS-in-JS
const Modal = AntModal as any;

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Icon mapping for dynamic icon rendering
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  AimOutlined,
  BulbOutlined,
  SendOutlined,
  CalendarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  ReloadOutlined,
  BankOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  CodeOutlined,
  CrownOutlined,
  SolutionOutlined,
  TrophyOutlined,
  DollarOutlined,
  UserSwitchOutlined,
  GiftOutlined,
  ShopOutlined,
  ApiOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  AppstoreOutlined,
  FilterOutlined,
  UserOutlined,
  SafetyOutlined,
  ExperimentOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  NotificationOutlined,
  FundOutlined,
  GlobalOutlined,
  CommentOutlined,
  HeartOutlined,
  LikeOutlined,
  BarChartOutlined,
  CloudOutlined,
  ToolOutlined,
  DisconnectOutlined,
  BellOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  ShareAltOutlined,
  FireOutlined,
  UndoOutlined,
  EyeInvisibleOutlined,
  SwapOutlined,
  RiseOutlined,
};

// Difficulty badge colors
const DIFFICULTY_COLORS = {
  beginner: { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  intermediate: { bg: "#fef3c7", text: "#b45309", border: "#fcd34d" },
  advanced: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
};

// Category type colors
const CATEGORY_COLORS: Record<CampaignCategory, string> = {
  prospecting: "#3b82f6",
  followup: "#10b981",
  meeting: "#8b5cf6",
  event: "#f59e0b",
  abm: "#ef4444",
  reengagement: "#6366f1",
  industry: "#ec4899",
  partner: "#06b6d4",
};

// Target audience colors
const AUDIENCE_COLORS: Record<TargetAudience, string> = {
  "b2b-saas": "#3b82f6",
  "agency": "#8b5cf6",
  "enterprise": "#ef4444",
  "generic": "#6b7280",
  "startup": "#10b981",
  "ecommerce": "#f59e0b",
};

// Personalization level colors
const PERSONALIZATION_COLORS: Record<PersonalizationLevel, { bg: string; text: string; border: string }> = {
  basic: { bg: "#e5e7eb", text: "#374151", border: "#d1d5db" },
  moderate: { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" },
  high: { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  hyper: { bg: "#fce7f3", text: "#be185d", border: "#f9a8d4" },
};

interface CampaignTemplateGalleryProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (template: CampaignTemplate) => void;
}

const CampaignTemplateGallery: React.FC<CampaignTemplateGalleryProps> = ({
  visible,
  onClose,
  onSelect,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedAudience, setSelectedAudience] = useState<string>("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("all");
  const [selectedPersonalization, setSelectedPersonalization] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter templates based on all criteria
  const filteredTemplates = useMemo(() => {
    return ALL_CAMPAIGN_TEMPLATES.filter((template) => {
      const matchesCategory =
        selectedCategory === "all" || template.category === selectedCategory;
      const matchesAudience =
        selectedAudience === "all" || template.targetAudience === selectedAudience;
      const matchesDifficulty =
        selectedDifficulty === "all" || template.difficulty === selectedDifficulty;
      const matchesPersonalization =
        selectedPersonalization === "all" ||
        template.personalizationLevel === selectedPersonalization;
      const matchesSearch =
        searchQuery === "" ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        ) ||
        template.bestFor.some((bf) =>
          bf.toLowerCase().includes(searchQuery.toLowerCase())
        );
      return (
        matchesCategory &&
        matchesAudience &&
        matchesDifficulty &&
        matchesPersonalization &&
        matchesSearch
      );
    });
  }, [selectedCategory, selectedAudience, selectedDifficulty, selectedPersonalization, searchQuery]);

  // Render icon component dynamically
  const renderIcon = (iconName: string, style: React.CSSProperties = {}) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent style={style} /> : <AimOutlined style={style} />;
  };

  // Handle template selection
  const handleTemplateClick = (template: CampaignTemplate) => {
    setSelectedTemplate(template);
  };

  // Handle use template
  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      setSelectedTemplate(null);
      setSelectedCategory("all");
      setSelectedAudience("all");
      setSelectedDifficulty("all");
      setSelectedPersonalization("all");
      setSearchQuery("");
      onClose();
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedAudience("all");
    setSelectedDifficulty("all");
    setSelectedPersonalization("all");
    setSearchQuery("");
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedCategory !== "all" ||
    selectedAudience !== "all" ||
    selectedDifficulty !== "all" ||
    selectedPersonalization !== "all" ||
    searchQuery !== "";

  // Template Card Component
  const TemplateCard = ({ template }: { template: CampaignTemplate }) => {
    const isSelected = selectedTemplate?.id === template.id;
    const difficultyStyle = DIFFICULTY_COLORS[template.difficulty];
    const categoryColor = CATEGORY_COLORS[template.category] || "#6b7280";
    const audienceColor = AUDIENCE_COLORS[template.targetAudience] || "#6b7280";

    return (
      <Card
        hoverable
        onClick={() => handleTemplateClick(template)}
        style={{
          height: "100%",
          border: isSelected ? `2px solid ${categoryColor}` : "1px solid #e5e7eb",
          borderRadius: 12,
          transition: "all 0.2s ease",
          boxShadow: isSelected
            ? `0 4px 12px ${categoryColor}33`
            : "0 1px 3px rgba(0,0,0,0.08)",
          background: isSelected ? `${categoryColor}08` : "#fff",
          position: "relative",
        }}
        bodyStyle={{ padding: 16 }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${categoryColor}20, ${categoryColor}40)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {renderIcon(template.icon, { fontSize: 24, color: categoryColor })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 15,
                display: "block",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {template.name}
            </Text>
            <Space size={4} wrap>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${categoryColor}40`,
                  background: `${categoryColor}15`,
                  color: categoryColor,
                  fontSize: 10,
                  padding: "0 5px",
                  margin: 0,
                }}
              >
                {template.category}
              </Tag>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${difficultyStyle.border}`,
                  background: difficultyStyle.bg,
                  color: difficultyStyle.text,
                  fontSize: 10,
                  padding: "0 5px",
                  margin: 0,
                }}
              >
                {template.difficulty}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Description */}
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{
            fontSize: 13,
            color: "#6b7280",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {template.description}
        </Paragraph>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Space size={12}>
            <Tooltip title="Emails in campaign">
              <Space size={4}>
                <MailOutlined style={{ color: "#9ca3af", fontSize: 13 }} />
                <Text style={{ fontSize: 12, color: "#6b7280" }}>
                  {template.emails.length}
                </Text>
              </Space>
            </Tooltip>
            <Tooltip title="Target audience">
              <Space size={4}>
                <UserOutlined style={{ color: "#9ca3af", fontSize: 13 }} />
                <Text style={{ fontSize: 12, color: "#6b7280" }}>
                  {template.targetAudience}
                </Text>
              </Space>
            </Tooltip>
            <Tooltip title="Setup time">
              <Space size={4}>
                <ClockCircleOutlined style={{ color: "#9ca3af", fontSize: 13 }} />
                <Text style={{ fontSize: 12, color: "#6b7280" }}>
                  {template.setupTime}
                </Text>
              </Space>
            </Tooltip>
          </Space>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: categoryColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlined style={{ color: "#fff", fontSize: 14 }} />
          </div>
        )}
      </Card>
    );
  };

  // Template Detail Panel
  const TemplateDetailPanel = ({ template }: { template: CampaignTemplate | null }) => {
    if (!template) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text style={{ color: "#9ca3af", display: "block", marginBottom: 8 }}>
                  Select a template to view details
                </Text>
                <Text style={{ fontSize: 12, color: "#d1d5db" }}>
                  Choose from {ALL_CAMPAIGN_TEMPLATES.length} cold outreach campaign templates
                </Text>
              </div>
            }
          />
        </div>
      );
    }

    const categoryColor = CATEGORY_COLORS[template.category] || "#6b7280";
    const personalizationStyle = PERSONALIZATION_COLORS[template.personalizationLevel];

    return (
      <div style={{ padding: 24, height: "100%", overflow: "auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${categoryColor}20, ${categoryColor}40)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderIcon(template.icon, { fontSize: 28, color: categoryColor })}
            </div>
            <div>
              <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                {template.name}
              </Title>
              <Space wrap>
                <Tag color={categoryColor}>{template.category}</Tag>
                <Tag>{template.difficulty}</Tag>
                <Tag
                  style={{
                    background: personalizationStyle.bg,
                    color: personalizationStyle.text,
                    border: `1px solid ${personalizationStyle.border}`,
                  }}
                >
                  {template.personalizationLevel} personalization
                </Tag>
              </Space>
            </div>
          </div>
          <Paragraph style={{ color: "#6b7280", marginBottom: 0 }}>
            {template.description}
          </Paragraph>
        </div>

        {/* Tabs for organized content */}
        <Tabs defaultActiveKey="strategy" size="small">
          <TabPane tab="Strategy" key="strategy">
            {/* Strategy */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <Text style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6 }}>
                  {template.strategy}
                </Text>
              </div>
            </div>

            {/* Expected Outcomes */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                Expected Outcomes
              </Text>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {template.expectedOutcomes.map((outcome, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <CheckCircleOutlined style={{ color: "#10b981", marginTop: 3, fontSize: 14 }} />
                    <Text style={{ fontSize: 13, color: "#4b5563" }}>{outcome}</Text>
                  </div>
                ))}
              </Space>
            </div>

            {/* Best Practices */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                Best Practices
              </Text>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {template.bestPractices.map((practice, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <StarOutlined style={{ color: "#f59e0b", marginTop: 3, fontSize: 14 }} />
                    <Text style={{ fontSize: 13, color: "#4b5563" }}>{practice}</Text>
                  </div>
                ))}
              </Space>
            </div>

            {/* Objection Handling */}
            {template.objectionHandling && template.objectionHandling.length > 0 && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                  Objection Handling
                </Text>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {template.objectionHandling.map((objection, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        background: "#fef3c7",
                        padding: 8,
                        borderRadius: 6,
                      }}
                    >
                      <CommentOutlined style={{ color: "#b45309", marginTop: 3, fontSize: 14 }} />
                      <Text style={{ fontSize: 12, color: "#92400e" }}>{objection}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            )}
          </TabPane>

          <TabPane tab={`Emails (${template.emails.length})`} key="emails">
            {/* Email Sequence Preview */}
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {template.emails.map((email, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: 12,
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: categoryColor,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {email.name}
                      </Text>
                      <Tag style={{ margin: 0, fontSize: 10 }}>
                        +{email.delayAfterPrevious.value} {email.delayAfterPrevious.unit}
                      </Tag>
                    </div>
                    <Text
                      style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}
                    >
                      Subject: {email.subject}
                    </Text>
                    {email.purpose && (
                      <Text style={{ fontSize: 11, color: "#9ca3af" }}>
                        {email.purpose}
                      </Text>
                    )}
                    {email.optimalSendTime && (
                      <div style={{ marginTop: 4 }}>
                        <Tag style={{ fontSize: 10 }}>
                          Best: {email.optimalSendTime.dayOfWeek.slice(0, 2).join(", ")} {email.optimalSendTime.timeSlot}
                        </Tag>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Space>
          </TabPane>

          <TabPane tab="Segmentation" key="segmentation">
            {/* Segmentation Info */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                Target Audience: {template.targetAudience}
              </Text>

              {template.segmentation.industries && template.segmentation.industries.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
                    Industries:
                  </Text>
                  <Space wrap size={4}>
                    {template.segmentation.industries.map((industry) => (
                      <Tag key={industry} style={{ fontSize: 11 }}>
                        {industry}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {template.segmentation.companySize && template.segmentation.companySize.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
                    Company Size:
                  </Text>
                  <Space wrap size={4}>
                    {template.segmentation.companySize.map((size) => (
                      <Tag key={size} style={{ fontSize: 11 }}>
                        {size}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {template.segmentation.jobTitles && template.segmentation.jobTitles.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
                    Job Titles:
                  </Text>
                  <Space wrap size={4}>
                    {template.segmentation.jobTitles.map((title) => (
                      <Tag key={title} style={{ fontSize: 11 }}>
                        {title}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {template.segmentation.leadSource && template.segmentation.leadSource.length > 0 && (
                <div>
                  <Text style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
                    Lead Sources:
                  </Text>
                  <Space wrap size={4}>
                    {template.segmentation.leadSource.map((source) => (
                      <Tag key={source} style={{ fontSize: 11 }}>
                        {source}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>

            {/* Sending Config */}
            {template.sendingConfig && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                  Recommended Sending Config
                </Text>
                <div
                  style={{
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Space direction="vertical" size={4}>
                    <Text style={{ fontSize: 12, color: "#4b5563" }}>
                      <strong>Emails/day/sender:</strong> {template.sendingConfig.emailsPerDayPerSender}
                    </Text>
                    <Text style={{ fontSize: 12, color: "#4b5563" }}>
                      <strong>Daily limit:</strong> {template.sendingConfig.dailyLimit}
                    </Text>
                    {template.sendingConfig.requiresWarming && (
                      <Tag color="orange" style={{ fontSize: 11 }}>
                        Requires email warming
                      </Tag>
                    )}
                  </Space>
                </div>
              </div>
            )}
          </TabPane>

          <TabPane tab="Info" key="info">
            {/* Tags */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                Tags
              </Text>
              <Space wrap size={4}>
                {template.tags.map((tag) => (
                  <Tag key={tag} style={{ borderRadius: 4 }}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            </div>

            {/* Best For */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                Best For
              </Text>
              <Space wrap size={4}>
                {template.bestFor.map((item) => (
                  <Tag key={item} color="blue" style={{ borderRadius: 4 }}>
                    {item}
                  </Tag>
                ))}
              </Space>
            </div>

            {/* Inspired By */}
            {template.inspiredBy && template.inspiredBy.length > 0 && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
                  Inspired By
                </Text>
                <Space wrap size={4}>
                  {template.inspiredBy.map((source) => (
                    <Tag key={source} color="purple" style={{ borderRadius: 4 }}>
                      {source}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </TabPane>
        </Tabs>
      </div>
    );
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      bodyStyle={{ padding: 0, height: "90vh", overflow: "hidden" }}
      style={{ top: 20 }}
      destroyOnClose
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Left Panel - Template List */}
        <div
          style={{
            width: "60%",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{ padding: 20, borderBottom: "1px solid #e5e7eb" }}>
            <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
              Choose a Campaign Template
            </Title>
            <Text style={{ color: "#6b7280" }}>
              {ALL_CAMPAIGN_TEMPLATES.length} cold outreach templates based on best practices from HubSpot, Outreach, Apollo & more
            </Text>
          </div>

          {/* Filters */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
            {/* Category Pills */}
            <Space wrap size={8} style={{ marginBottom: 16 }}>
              <Button
                type={selectedCategory === "all" ? "primary" : "default"}
                size="small"
                onClick={() => setSelectedCategory("all")}
                icon={<AppstoreOutlined />}
                style={{ borderRadius: 6 }}
              >
                All ({ALL_CAMPAIGN_TEMPLATES.length})
              </Button>
              {CAMPAIGN_TEMPLATE_CATEGORIES.map((category) => {
                const CategoryIcon = ICON_MAP[category.icon];
                return (
                  <Button
                    key={category.id}
                    type={selectedCategory === category.id ? "primary" : "default"}
                    size="small"
                    onClick={() => setSelectedCategory(category.id)}
                    icon={CategoryIcon ? <CategoryIcon /> : null}
                    style={{
                      borderRadius: 6,
                      borderColor:
                        selectedCategory === category.id
                          ? category.color
                          : undefined,
                      background:
                        selectedCategory === category.id
                          ? category.color
                          : undefined,
                    }}
                  >
                    {category.name} ({category.templates.length})
                  </Button>
                );
              })}
            </Space>

            {/* Search and Filter Toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: showFilters ? 12 : 0 }}>
              <Input
                placeholder="Search templates..."
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                style={{ borderRadius: 8, flex: 1 }}
              />
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters || hasActiveFilters ? "primary" : "default"}
                ghost={showFilters || hasActiveFilters}
                style={{ borderRadius: 8 }}
              >
                Filters {hasActiveFilters && `(${[selectedAudience, selectedDifficulty, selectedPersonalization].filter(v => v !== "all").length})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} style={{ borderRadius: 8 }}>
                  Clear
                </Button>
              )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <Row gutter={12}>
                <Col span={8}>
                  <Select
                    value={selectedAudience}
                    onChange={setSelectedAudience}
                    style={{ width: "100%" }}
                    placeholder="Target Audience"
                    size="small"
                  >
                    <Option value="all">All Audiences</Option>
                    <Option value="b2b-saas">B2B SaaS</Option>
                    <Option value="agency">Agency</Option>
                    <Option value="enterprise">Enterprise</Option>
                    <Option value="startup">Startup</Option>
                    <Option value="ecommerce">E-commerce</Option>
                    <Option value="generic">Generic</Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Select
                    value={selectedDifficulty}
                    onChange={setSelectedDifficulty}
                    style={{ width: "100%" }}
                    placeholder="Difficulty"
                    size="small"
                  >
                    <Option value="all">All Difficulties</Option>
                    <Option value="beginner">Beginner</Option>
                    <Option value="intermediate">Intermediate</Option>
                    <Option value="advanced">Advanced</Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Select
                    value={selectedPersonalization}
                    onChange={setSelectedPersonalization}
                    style={{ width: "100%" }}
                    placeholder="Personalization"
                    size="small"
                  >
                    <Option value="all">All Levels</Option>
                    <Option value="basic">Basic</Option>
                    <Option value="moderate">Moderate</Option>
                    <Option value="high">High</Option>
                    <Option value="hyper">Hyper</Option>
                  </Select>
                </Col>
              </Row>
            )}
          </div>

          {/* Template Grid */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {filteredTemplates.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredTemplates.map((template) => (
                  <Col key={template.id} xs={24} sm={12}>
                    <TemplateCard template={template} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty
                description={
                  <div>
                    <Text style={{ display: "block", marginBottom: 8 }}>No templates found</Text>
                    <Button type="link" onClick={clearFilters}>
                      Clear all filters
                    </Button>
                  </div>
                }
                style={{ marginTop: 60 }}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Template Details */}
        <div
          style={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div style={{ flex: 1, overflow: "auto" }}>
            <TemplateDetailPanel template={selectedTemplate} />
          </div>

          {/* Action Footer */}
          <div
            style={{
              padding: 16,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedTemplate}
              onClick={handleUseTemplate}
              style={{
                background: selectedTemplate
                  ? CATEGORY_COLORS[selectedTemplate.category]
                  : undefined,
                borderColor: selectedTemplate
                  ? CATEGORY_COLORS[selectedTemplate.category]
                  : undefined,
              }}
            >
              Use This Template
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignTemplateGallery;
